<script>
import {computed, onMounted, ref} from "vue";
import useValidation from "@/use/Validation";
import {useEmailStore} from "@/stores/Email.js";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const readonly = computed(() => props.deleteAction || props.viewAction);
        const {loading, overlay, dialog, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();
        const emailStore = useEmailStore();


        const email = ref({});


        function findById(id) {
            loading.value = true;
            axios.get(`/emails/${id}`).then((response) => {
                email.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/emails', email.value).then((response) => {
                    emailStore.addEmail(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/emails', email.value).then((response) => {
                    emailStore.updateEmail(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/emails/${email.value.id}`).then(() => {
                emailStore.deleteEmail(email.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        })

        return {
            mxRules,
            readonly,
            emailStore,
            loading,
            overlay,
            dialog,
            inputColor,
            backgroundColor,
            email,
            save,
            update,
            remove,
            getBack,
        }
    },
};
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        v-model="dialog"
        persistent
        max-width="800"
        scrollable
    >
        <v-card flat>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @cancel="overlay = false"
                @remove="remove"
            ></w-overlay>
            <v-card-text>
                <v-container fluid>
                    <v-form ref="form" lazy-validation>
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="6">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="email.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('common.email')" required/>
                                    <w-text-field
                                        v-model="email.address"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.password')" required/>
                                    <w-password-text-field
                                        v-model="email.password"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('email.incoming_server')" required/>
                                    <w-text-field
                                        v-model="email.incomingServer"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('email.incoming_port')" required/>
                                    <w-text-field
                                        v-model="email.incomingPort"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('email.outgoing_server')" required/>
                                    <w-text-field
                                        v-model="email.outgoingServer"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('email.outgoing_port')" required/>
                                    <w-text-field
                                        v-model="email.outgoingPort"
                                        type="number"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>